import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import { APP_ENV, resolveAppUrl } from '../config/app-environment';
import { AuthenticatedUser } from '../models/hospitality';
import { ApiActivityService } from '../services/api-activity.service';
import { extractPrimaryUserRole, normalizeUserRole } from './role.utils';

type KeycloakModule = typeof import('keycloak-js');
type KeycloakClient = InstanceType<KeycloakModule['default']>;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly environment = inject(APP_ENV);
  private readonly activity = inject(ApiActivityService);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly authenticated = signal(false);
  readonly user = signal<AuthenticatedUser | null>(null);
  readonly accessToken = signal<string | null>(null);

  private initialized = false;
  private keycloak: KeycloakClient | null = null;
  private sessionExpiryRedirectInFlight = false;
  private forceLoginPrompt = false;

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    if (this.environment.auth.mode === 'mock') {
      this.applyMockSession();
      this.loading.set(false);
      return;
    }

    try {
      const keycloakModule = await import('keycloak-js');
      const Keycloak = keycloakModule.default;

      this.keycloak = new Keycloak({
        url: this.environment.auth.keycloakUrl,
        realm: this.environment.auth.realm,
        clientId: this.environment.auth.clientId
      });

      const authenticated = await this.activity.track(() =>
        this.keycloak!.init({
          checkLoginIframe: false,
          pkceMethod: 'S256',
          redirectUri: resolveAppUrl(this.environment.auth.redirectUri)
        })
      );

      this.authenticated.set(authenticated);

      if (authenticated) {
        await this.syncKeycloakSession();
      }

      if (this.keycloak) {
        this.keycloak.onAuthSuccess = () => {
          void this.syncKeycloakSession();
        };

        this.keycloak.onAuthRefreshSuccess = () => {
          void this.syncKeycloakSession();
        };

        this.keycloak.onAuthLogout = () => {
          this.clearSession();
        };

        this.keycloak.onTokenExpired = () => {
          void this.ensureValidToken();
        };
      }
    } catch (error) {
      console.error('Keycloak initialization failed. Falling back to logged-out state.', error);
      this.clearSession();
    } finally {
      this.loading.set(false);
    }
  }

  authMode(): 'mock' | 'keycloak' {
    return this.environment.auth.mode;
  }

  async ensureAuthenticated(redirectTo: string): Promise<boolean> {
    if (!this.initialized) {
      await this.init();
    }

    if (this.authenticated()) {
      return true;
    }

    if (this.environment.auth.mode === 'keycloak') {
      await this.login(redirectTo);
      return false;
    }

    return false;
  }

  async login(redirectTo = window.location.href): Promise<void> {
    if (this.environment.auth.mode === 'mock') {
      this.applyMockSession();
      return;
    }

    const prompt = this.forceLoginPrompt ? 'login' : undefined;
    this.forceLoginPrompt = false;

    await this.activity.track(() =>
      this.keycloak?.login({
        redirectUri: new URL(redirectTo, window.location.origin).toString(),
        ...(prompt ? { prompt } : {})
      }) ?? Promise.resolve()
    );
  }

  async logout(redirectTo = ''): Promise<void> {
    if (this.environment.auth.mode === 'mock') {
      this.clearSession();
      return;
    }

    const resolvedRedirect = resolveAppUrl(
      redirectTo || this.environment.auth.logoutRedirectUri
    );

    await this.activity.track(() =>
      this.keycloak?.logout({
        redirectUri: resolvedRedirect
      }) ?? Promise.resolve()
    );

    this.clearSession();
  }

  async handleSessionExpired(): Promise<void> {
    this.keycloak?.clearToken();
    this.clearSession();
    this.forceLoginPrompt = true;

    if (this.environment.auth.mode !== 'keycloak') {
      return;
    }

    const currentUrl = this.router.url || '/';

    if (currentUrl.startsWith('/auth/login') || this.sessionExpiryRedirectInFlight) {
      return;
    }

    this.sessionExpiryRedirectInFlight = true;

    await this.router.navigate(['/auth/login'], {
      replaceUrl: true,
      queryParams: currentUrl ? { returnUrl: currentUrl } : undefined
    });
  }

  async ensureValidToken(minValidity = 30): Promise<string | null> {
    if (this.environment.auth.mode === 'mock') {
      return this.accessToken();
    }

    if (!this.keycloak || !this.authenticated()) {
      return this.accessToken();
    }

    try {
      const refreshed = await this.activity.track(() => this.keycloak!.updateToken(minValidity));
      const token = this.keycloak.token ?? null;

      if (refreshed) {
        await this.syncKeycloakSession();
      } else {
        this.accessToken.set(token);
      }

      return token;
    } catch {
      await this.handleSessionExpired();
      return null;
    }
  }

  private applyMockSession(): void {
    const normalizedRole = normalizeUserRole(this.environment.demoUser.role) ?? this.environment.demoUser.role;

    this.user.set({
      id: this.environment.demoUser.id,
      name: this.environment.demoUser.name,
      email: this.environment.demoUser.email,
      role: normalizedRole,
      realmRoles: [normalizedRole],
      tenantIds: this.environment.demoUser.tenantIds,
      locationIds: this.environment.demoUser.locationIds,
      mode: 'mock'
    });
    this.accessToken.set('mock-access-token');
    this.authenticated.set(true);
  }

  private async syncKeycloakSession(): Promise<void> {
    if (!this.keycloak) {
      return;
    }

    const profile = await this.activity.track(() => this.keycloak!.loadUserProfile());
    const parsedToken = this.keycloak.tokenParsed as
      | {
          realm_access?: { roles?: string[] };
          resource_access?: Record<string, { roles?: string[] }>;
          email?: string;
          name?: string;
          tenantIds?: string[];
          tenant_ids?: string[];
          locationIds?: string[];
          location_ids?: string[];
          scopes?: Array<{
            tenantId?: string;
            tenant_id?: string;
            locationIds?: string[];
            location_ids?: string[];
          }>;
        }
      | undefined;
    const tokenRoles = [
      ...(parsedToken?.realm_access?.roles ?? []),
      ...(parsedToken?.resource_access?.[this.environment.auth.clientId]?.roles ?? [])
    ];
    const normalizedRealmRoles = [...new Set(tokenRoles)].map(
      (role) => normalizeUserRole(role) ?? role
    );
    const normalizedRole = extractPrimaryUserRole(tokenRoles) ?? 'authenticated-user';
    const tenantIds = this.extractTenantIds(parsedToken);
    const locationIds = this.extractLocationIds(parsedToken);

    this.user.set({
      id: profile.id ?? 'keycloak-user',
      name:
        parsedToken?.name ??
        [profile.firstName, profile.lastName].filter(Boolean).join(' ') ??
        profile.username ??
        'Keycloak user',
      email: profile.email ?? parsedToken?.email ?? '',
      role: normalizedRole,
      realmRoles: normalizedRealmRoles,
      tenantIds,
      locationIds,
      mode: 'keycloak'
    });
    this.accessToken.set(this.keycloak.token ?? null);
    this.authenticated.set(true);
    this.sessionExpiryRedirectInFlight = false;
  }

  private clearSession(): void {
    this.user.set(null);
    this.accessToken.set(null);
    this.authenticated.set(false);
  }

  private extractTenantIds(
    token:
      | {
          tenantIds?: string[];
          tenant_ids?: string[];
          scopes?: Array<{ tenantId?: string; tenant_id?: string }>;
        }
      | undefined
  ): string[] {
    const directTenantIds = token?.tenantIds ?? token?.tenant_ids ?? [];
    const scopedTenantIds = (token?.scopes ?? []).flatMap((scope) => scope.tenantId ?? scope.tenant_id ?? []);

    return [...new Set([...directTenantIds, ...scopedTenantIds])];
  }

  private extractLocationIds(
    token:
      | {
          locationIds?: string[];
          location_ids?: string[];
          scopes?: Array<{ locationIds?: string[]; location_ids?: string[] }>;
        }
      | undefined
  ): string[] {
    const directLocationIds = token?.locationIds ?? token?.location_ids ?? [];
    const scopedLocationIds = (token?.scopes ?? []).flatMap(
      (scope) => scope.locationIds ?? scope.location_ids ?? []
    );

    return [...new Set([...directLocationIds, ...scopedLocationIds])];
  }
}
